import { UploadCloud02 } from '@untitledui/icons';
import { useCallback, useRef, useState } from 'react';

import { DropArea } from '@/lib/base/card';
import { MAX_FILE_SIZE_MB } from '@/lib/files/constants';
import { cx } from '@/utils/cx';

export interface FileDropZoneProps {
  /** Called when files are selected via drag-drop or file picker. */
  onFilesSelected: (files: FileList) => void;
  /** Accepted file types (passed to the file input's `accept` attribute). */
  acceptedTypes?: string;
  /** Whether multiple files can be selected. @default true */
  multiple?: boolean;
  /** Whether the drop zone is disabled. */
  isDisabled?: boolean;
  /** Hint text shown below the main label. @default `Maximum file size: ${MAX_FILE_SIZE_MB} MB` */
  hint?: string;
  /** Additional class name. */
  className?: string;
}

/**
 * A presentational drag-and-drop file upload zone.
 *
 * Uses DropArea for the hover-highlight border, which provides consistent
 * drag-over styling across the app (dashed border → brand on hover).
 *
 * Handles drag state visuals and file selection — callers supply the
 * `onFilesSelected` callback to process the files however they need
 * (upload to server, read locally, etc.).
 */
export function FileDropZone({
  onFilesSelected,
  acceptedTypes,
  multiple = true,
  isDisabled = false,
  hint = `Maximum file size: ${MAX_FILE_SIZE_MB} MB`,
  className,
}: FileDropZoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      if (!isDisabled && e.dataTransfer.files.length > 0) {
        onFilesSelected(e.dataTransfer.files);
      }
    },
    [isDisabled, onFilesSelected]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (!isDisabled) {
        setIsDragging(true);
      }
    },
    [isDisabled]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        onFilesSelected(e.target.files);
      }
      // Reset input so the same file can be re-selected
      e.target.value = '';
    },
    [onFilesSelected]
  );

  const handleClick = useCallback(() => {
    if (!isDisabled) {
      fileInputRef.current?.click();
    }
  }, [isDisabled]);

  return (
    <DropArea
      isOver={isDragging}
      showBorder
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onClick={handleClick}
      className={cx(
        'cursor-pointer hover:border-border-primary',
        isDisabled && 'opacity-50 cursor-not-allowed',
        className
      )}
    >
      <div className='px-6 py-8 text-center'>
        <div className='flex justify-center mb-3'>
          <div
            className={cx(
              'p-3 rounded-lg',
              isDragging
                ? 'bg-[var(--color-brand-100)]'
                : 'bg-[var(--color-bg-secondary)]'
            )}
          >
            <UploadCloud02 className='size-6 text-[var(--color-fg-quaternary)]' />
          </div>
        </div>
        <div className='mb-1'>
          <span className='text-sm font-medium text-[var(--color-fg-brand-primary)]'>
            Click to upload
          </span>
          <span className='text-sm text-[var(--color-text-tertiary)]'>
            {' '}
            or drag and drop
          </span>
        </div>
        {hint && (
          <div className='text-xs text-[var(--color-text-tertiary)]'>
            {hint}
          </div>
        )}
      </div>
      <input
        ref={fileInputRef}
        type='file'
        className='hidden'
        onChange={handleFileSelect}
        accept={acceptedTypes}
        multiple={multiple}
        disabled={isDisabled}
      />
    </DropArea>
  );
}
